import React, { useState } from "react";
import {
  Row,
  Col,
  Typography,
  Button,
  Input,
  Table,
  Dropdown,
  Space,
  Badge,
} from "antd";
import {
  SearchOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import "./ShopReimbursements.css";

const { Title } = Typography;

interface ReimbursementData {
  key: string;
  dateSubmitted: string;
  shopName: string;
  trackingNumber: string;
  traceId: string;
  checkTrackingNumber: string;
  safetyGearIncentive: string;
  totalIncentiveAmount: string;
  customerName: string;
  customerIncentiveCode: string;
  dateOfSale: string;
  incentiveCode: string;
  phone: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
}

const ShopReimbursements: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [activeTab, setActiveTab] = useState("pending");

  // Sample data - in a real app, this would come from an API
  const data: ReimbursementData[] = Array.from({ length: 10 }, (_, i) => ({
    key: `${i + 1}`,
    dateSubmitted:
      i < 3
        ? "03/24/2025"
        : i < 5
          ? "03/25/2025"
          : i < 8
            ? "03/22/2025"
            : "03/21/2025",
    shopName: [
      "Trek Bicycles",
      "Portland Bicycle Emporium",
      "Cyclepath",
      "Bike Farm",
      "Clever Cycles",
    ][i % 5],
    trackingNumber: ["2001826523", "1834309911", "8749256186"][i % 3],
    traceId: ["8967924916", "8967927798"][i % 2],
    checkTrackingNumber: ["8967924916", "8967927798"][i % 2],
    safetyGearIncentive: "$150",
    totalIncentiveAmount:
      i === 3 || i === 5 || i === 8
        ? "$650"
        : i === 9 || i === 11
          ? "$1,650"
          : "$1,150",
    customerName: [
      "Kamron Gunderson",
      "Jessica Dinsmoor",
      "Noor Wolfe",
      "Robert Parr",
      "Alice Robert",
      "Devin Clark",
      "Abigail Wingen",
      "Katie Earlandson",
      "Carlos Tolentino",
      "Abraham Garcia",
    ][i],
    customerIncentiveCode: ["W1gb0G", "EABD3i", "W0UA3j", "9NVjDQ", "qNDVPf"][
      i % 5
    ],
    dateOfSale:
      i < 3
        ? "03/24/2025"
        : i < 5
          ? "03/25/2025"
          : i < 8
            ? "03/22/2025"
            : "03/21/2025",
    incentiveCode: "sampleemail@email.com",
    phone: "555 123 4321",
    address1: [
      "42 Windmill Ln",
      "1234 Sunshine Dr",
      "542 Aloma",
      "2 Aruba",
      "32485 Walter St",
      "3258 Oak",
      "2 Ridge Route",
      "99443 Bloomington Ct",
      "56 Estrella",
      "52833 Adelanto",
    ][i],
    address2: i === 4 || i === 9 ? (i === 4 ? "56 C" : "Suite 100") : undefined,
    city: "Portland",
    state: "OR",
    zip: "222222",
  }));

  const columns: ColumnsType<ReimbursementData> = [
    {
      title: "Bike Shop Name",
      dataIndex: "shopName",
      key: "shopName",
      width: 200,
      fixed: "left",
    },
    {
      title: "Decline Reason",
      key: "declineReason",
      width: 180,
      render: () => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "1", label: "Incomplete Documentation" },
              { key: "2", label: "Invalid Purchase" },
              { key: "3", label: "Duplicate Request" },
            ],
          }}
        >
          <Button
            style={{
              border: "1px solid #DDD",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            Decline Reason
            <DownOutlined />
          </Button>
        </Dropdown>
      ),
    },
    {
      title: "Tracking Number",
      dataIndex: "trackingNumber",
      key: "trackingNumber",
      width: 150,
    },
    {
      title: "Trace ID",
      dataIndex: "traceId",
      key: "traceId",
      width: 120,
    },
    {
      title: "Check Tracking Number",
      dataIndex: "checkTrackingNumber",
      key: "checkTrackingNumber",
      width: 180,
    },
    {
      title: "Safety Gear Incentive",
      dataIndex: "safetyGearIncentive",
      key: "safetyGearIncentive",
      width: 160,
    },
    {
      title: "Total Incentive Amount",
      dataIndex: "totalIncentiveAmount",
      key: "totalIncentiveAmount",
      width: 180,
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      width: 160,
    },
    {
      title: "Date Submitted",
      dataIndex: "dateSubmitted",
      key: "dateSubmitted",
      width: 140,
    },
    {
      title: "Customer Incentive Code",
      dataIndex: "customerIncentiveCode",
      key: "customerIncentiveCode",
      width: 180,
    },
    {
      title: "Date of Sale",
      dataIndex: "dateOfSale",
      key: "dateOfSale",
      width: 120,
    },
    {
      title: "Incentive Code",
      dataIndex: "incentiveCode",
      key: "incentiveCode",
      width: 180,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 120,
    },
    {
      title: "Address 1",
      dataIndex: "address1",
      key: "address1",
      width: 160,
    },
    {
      title: "Address 2",
      dataIndex: "address2",
      key: "address2",
      width: 120,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: 100,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 80,
    },
    {
      title: "Zip",
      dataIndex: "zip",
      key: "zip",
      width: 80,
    },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const tabItems = [
    {
      key: "pending",
      icon: "/images/pending-icon.svg",
      label: "Pending Shop Reimbursements",
      count: 162,
      active: true,
    },
    {
      key: "all",
      icon: "/images/shop-reimbursements-icon.svg",
      label: "All Shop Reimbursements",
      count: null,
      active: false,
    },
    {
      key: "approved",
      icon: "/images/approved-icon.svg",
      label: "Approved",
      count: null,
      active: false,
    },
    {
      key: "funded",
      icon: "/images/funded-icon.svg",
      label: "Funded",
      count: null,
      active: false,
    },
    {
      key: "declined",
      icon: "/images/declined-icon.svg",
      label: "Declined",
      count: null,
      active: false,
    },
  ];

  return (
    <div className="shop-reimbursements-page">
      {/* Page Header */}
      <div className="page-header">
        <Space size={10} align="center">
          <img
            src="/images/shop-icon.png"
            alt="Shop Reimbursements"
            className="page-icon"
          />
          <Title level={2} className="page-title">
            Bike Shop Reimbursements
          </Title>
        </Space>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <div className="tab-items">
          {tabItems.map((tab) => (
            <div
              key={tab.key}
              className={`tab-item ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <Space size={6} align="center">
                <img src={tab.icon} alt={tab.label} className="tab-icon" />
                <span className="tab-label">{tab.label}</span>
                {tab.count && (
                  <Badge
                    count={tab.count}
                    style={{
                      backgroundColor: "#007AFF",
                      fontSize: "11px",
                      fontWeight: 500,
                    }}
                  />
                )}
              </Space>
            </div>
          ))}
        </div>
        <div className="tab-indicator" />
        <div className="tab-separator" />
      </div>

      {/* Action Bar */}
      <Row
        justify="space-between"
        align="middle"
        className="action-bar"
        gutter={[16, 16]}
      >
        <Col xs={24} sm={24} md={16} lg={18}>
          <Space size={10} wrap>
            <Button
              type="primary"
              ghost
              style={{
                borderColor: "#389E0D",
                color: "#389E0D",
              }}
            >
              Approve
            </Button>
            <Button
              danger
              ghost
              style={{
                borderColor: "#CF1322",
                color: "#CF1322",
              }}
            >
              Decline
            </Button>
            <span className="pending-count">
              162 Pending Bike Shop Reimbursements
            </span>
          </Space>
        </Col>
        <Col xs={24} sm={24} md={8} lg={6}>
          <Input
            placeholder="Search"
            suffix={<SearchOutlined />}
            className="search-input"
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      {/* Data Table */}
      <div className="table-container">
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={data}
          pagination={false}
          scroll={{ x: 2000, y: 400 }}
          size="small"
          className="reimbursements-table"
          sticky={{ offsetHeader: 0 }}
        />
      </div>

      {/* Pagination */}
      <Row justify="end" className="pagination-container">
        <Col xs={24} sm={24} md="auto">
          <Space
            size={21}
            align="center"
            style={{ justifyContent: "center", width: "100%" }}
          >
            <Button
              icon={<LeftOutlined />}
              type="text"
              className="pagination-arrow"
            />
            <Button type="primary" className="pagination-current">
              1
            </Button>
            <Button type="text" className="pagination-number">
              2
            </Button>
            <Button type="text" className="pagination-number">
              3
            </Button>
            <Button type="text" className="pagination-number">
              4
            </Button>
            <Button
              icon={<RightOutlined />}
              type="text"
              className="pagination-arrow"
            />
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default ShopReimbursements;
